import React from 'react';
import type { JSX } from 'react';
import { Info, Radio, RotateCcw, Sparkles } from 'lucide-react';
import type { Ebene, VerortungsDiagnose } from '../../services/verortung';

/**
 * Automatische Verortung im Wizard-Schritt „Wo“ (Betreiber 27.09.: „keine
 * automatische Verortung, man muss den Ort eintippen“):
 *
 *   • automatisch eingetragener Ort mit Herkunft und „Rückgängig“,
 *   • Geschoss-Knöpfe: ein Tipp setzt den Ort UND den Nullpunkt (einmal je
 *     Begehung nötig, danach läuft die Schätzung von selbst),
 *   • warum es nicht automatisch geht (Diagnose), wenn es nicht geht.
 */

export interface OrtAutomatikProps {
    diagnose: VerortungsDiagnose;
    /** Automatisch eingetragener Ort, solange das Ortsfeld noch genau ihn enthält. */
    auto: { text: string; quellen: string[] } | null;
    onRueckgaengig: () => void;
    geschosse: readonly Ebene[];
    lagen: readonly Ebene[];
    /** Live geschätzte Ebene (Hervorhebung). */
    geschaetztId: string | null;
    /** „Live: vermutlich 2. OG · GPS ±4 m“ bzw. „Wo sind Sie? …“ */
    liveText: string;
    onEbene: (ebene: Ebene) => void;
}

const Chip: React.FC<{ ebene: Ebene; aktiv: boolean; onTippen: () => void }> = ({ ebene, aktiv, onTippen }) => (
    <button
        type="button"
        onClick={onTippen}
        aria-pressed={aktiv}
        className={`min-w-[3.5rem] rounded-xl border-2 px-3 py-2.5 text-sm font-bold active:scale-95 transition-transform ${aktiv ? 'bg-sky-600 border-sky-600 text-white' : 'bg-white border-slate-200 text-slate-700'}`}
    >
        {ebene.art === 'geruest' ? ebene.name.replace(/^Gerüst\s+/, '') : ebene.name}
    </button>
);

export function OrtAutomatik({ diagnose, auto, onRueckgaengig, geschosse, lagen, geschaetztId, liveText, onEbene }: OrtAutomatikProps): JSX.Element | null {
    if (!diagnose.nutzbar) {
        if (!diagnose.grund) return null;
        return (
            <p className="text-xs text-slate-500 bg-slate-100 border border-slate-200 rounded-xl px-3 py-2 flex items-start gap-2">
                <Info className="w-4 h-4 shrink-0 mt-0.5" />
                <span><span className="font-semibold">Automatische Verortung aus:</span> {diagnose.grund}</span>
            </p>
        );
    }
    const oben = (liste: readonly Ebene[]) => [...liste].sort((a, b) => b.hoeheM - a.hoeheM);
    return (
        <div className="space-y-3">
            {auto && (
                <div className="rounded-xl border-2 border-emerald-300 bg-emerald-50 px-3 py-2.5 flex items-start gap-2">
                    <Sparkles className="w-4 h-4 text-emerald-700 shrink-0 mt-0.5" />
                    <div className="min-w-0 flex-1">
                        <p className="text-sm font-bold text-emerald-900">Automatisch eingetragen: {auto.text}</p>
                        <p className="text-[11px] text-emerald-800">
                            aus {auto.quellen.join(' + ') || 'Sensoren'} · beim Speichern gilt es als bestätigt
                        </p>
                    </div>
                    <button type="button" onClick={onRueckgaengig} className="relative treffer-44 shrink-0 text-xs font-bold text-emerald-900 underline flex items-center gap-1">
                        <RotateCcw className="w-3.5 h-3.5" /> Rückgängig
                    </button>
                </div>
            )}
            {(geschosse.length > 0 || lagen.length > 0) && (
                <div>
                    <p className="text-[11px] font-bold text-slate-400 uppercase flex items-center gap-1.5">
                        <Radio className="w-3.5 h-3.5 text-sky-500" /> {liveText}
                    </p>
                    <p className="text-[11px] text-slate-400 mb-2">Antippen setzt den Ort und stellt die Geschoss-Erkennung nach.</p>
                    {geschosse.length > 0 && (
                        <div className="flex flex-wrap gap-2">
                            {oben(geschosse).map((e) => <Chip key={e.id} ebene={e} aktiv={e.id === geschaetztId} onTippen={() => onEbene(e)} />)}
                        </div>
                    )}
                    {lagen.length > 0 && (
                        <>
                            <p className="text-[11px] font-bold text-slate-400 uppercase mt-3 mb-1">Gerüst</p>
                            <div className="flex flex-wrap gap-2">
                                {oben(lagen).map((e) => <Chip key={e.id} ebene={e} aktiv={e.id === geschaetztId} onTippen={() => onEbene(e)} />)}
                            </div>
                        </>
                    )}
                </div>
            )}
            {diagnose.hinweise.length > 0 && (
                <ul className="text-[11px] text-slate-400 list-disc pl-4">
                    {diagnose.hinweise.map((h) => <li key={h}>{h}</li>)}
                </ul>
            )}
        </div>
    );
}
